import type { DeskSnapshot } from '../api/generated/types.gen';
import { countdown, price, signed, tenths, time, toTenths } from '../format';
import type { Side } from './Ticket';

/**
 * The 15-minute horizon has one contract per window: will the final 60-second index average be at least the
 * average before the window opened? This replaces the strike rail. Recent results describe what happened; they
 * are not a forecast.
 */
export function WindowPanel({ desk, now, side, onPick }: { desk: DeskSnapshot; now: number; side: Side; onPick: (s: Side) => void }) {
  const c = desk.selected, d = desk.decimals ?? 2, index = desk.settlement_index ?? 'the index';
  const spot = desk.quotes.coinbase ? Number(desk.quotes.coinbase.price) : null;
  const target = c && c.comparator === 'at_least' ? Number(c.strike) : null;
  const distance = spot != null && target != null ? spot - target : null;
  const pct = distance != null && target ? distance / target * 100 : null;
  const close = c ? Date.parse(c.close_time) / 1000 : null;
  const remaining = close == null ? null : Math.max(0, Math.floor(close - now));
  const windows = desk.windows ?? [];
  const ups = windows.filter(w => w.result === 'yes').length;
  return <div className="rail window-panel" data-testid="window-panel" aria-label="This 15-minute window">
    {!c ? <div className="rail-empty">No open window. The next one appears once Kalshi lists it.</div> : <>
      <section className="win-now">
        <div className="win-head"><span>This window · {c.open_time ? `${time(c.open_time)}–` : ''}{time(c.close_time)}</span>
          <b className="num amber" data-testid="window-countdown">{remaining ? countdown(remaining) : 'Closed'}</b></div>
        <div className="win-target"><span className="faint">Target</span><b className="num amber" data-testid="window-target">{c.label}</b>
          <small>Set from {index} 60s average before {c.open_time ? time(c.open_time) : 'the window opened'}</small></div>
        <dl className="win-facts">
          <dt>Coinbase now</dt><dd className="num">{spot == null ? '—' : price(spot, d)}</dd>
          <dt>Spot vs target</dt><dd className={`num ${distance == null ? '' : distance >= 0 ? 'up' : 'down'}`}>{distance == null ? '—' : `${signed(distance, d)} (${signed(pct, 3, '%')})`}</dd>
        </dl>
        <p className="faint win-rule">UP wins if the final average is at least the target. Coinbase is a proxy, not {index}.</p>
        <div className="win-sides" role="group" aria-label="Side">
          <button className="up-btn" aria-pressed={side === 'yes'} onClick={() => onPick('yes')} data-testid="pick-up">
            <span>UP</span><b className="num">{tenths(toTenths(c.yes_ask))}</b><small>bid {tenths(toTenths(c.yes_bid))}</small></button>
          <button className="down-btn" aria-pressed={side === 'no'} onClick={() => onPick('no')} data-testid="pick-down">
            <span>DOWN</span><b className="num">{tenths(toTenths(c.no_ask))}</b><small>bid {tenths(toTenths(c.no_bid))}</small></button>
        </div>
      </section>
      <section className="win-next"><span className="faint">Next window · opens {time(c.close_time)}</span><span>Target: this window’s settlement average</span></section>
    </>}
    <section className="win-recent">
      <div className="win-head"><span>Recent windows</span><span className="faint">{windows.length ? `${ups} up · ${windows.length - ups} down` : ''}</span></div>
      {!windows.length ? <p className="faint">Settled windows appear here as Kalshi publishes them.</p>
        : <ol data-testid="recent-windows">{windows.map(w => {
          const moved = w.value != null ? Number(w.value) - Number(w.target) : null;
          return <li key={w.ticker}><span>{time(w.close_time)}</span>
            <b className={w.result === 'yes' ? 'up' : w.result === 'no' ? 'down' : 'faint'}>{w.result === 'yes' ? '▲' : w.result === 'no' ? '▼' : '·'}</b>
            <span className="num">{w.value ? price(w.value, d) : 'pending'}</span>
            <span className={`num ${moved == null ? '' : moved >= 0 ? 'up' : 'down'}`}>{moved == null ? '' : signed(moved, d)}</span></li>;
        })}</ol>}
    </section>
  </div>;
}
